import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCreateInternalProjectTask } from "@/hooks/use-internal-project-tasks";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  /** Kalles med den nyopprettede oppgaven (id) etter vellykket lagring, slik
   * at calleren kan velge den med det samme i stedet for å kreve et nytt klikk. */
  onCreated?: (taskId: string) => void;
}

/**
 * VIB-1208: lettvekts «Ny oppgave»-dialog for internprosjekter. Ingen
 * tilbud/budsjett-flyt — bare navn. Gjenbrukes fra NewRegistrationPopup,
 * RegistrationEditDialog og InternalProjectsSection (samme dialog, tre
 * inngangspunkter).
 */
export function CreateInternalTaskDialog({ open, onOpenChange, projectId, projectName, onCreated }: Props) {
  const [name, setName] = useState("");
  const createTask = useCreateInternalProjectTask();

  useEffect(() => {
    if (open) setName("");
  }, [open]);

  const handleSubmit = () => {
    if (!name.trim()) return;
    createTask.mutate(
      { project_id: projectId, description: name },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          if (data?.id) onCreated?.(data.id);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Ny oppgave</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Prosjekt</Label>
            <p className="text-sm text-muted-foreground">{projectName}</p>
          </div>
          <div>
            <Label htmlFor="internal-task-name" className="text-xs">
              Oppgavenavn
            </Label>
            <Input
              id="internal-task-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="f.eks. Fakturering, Møter, Treatment"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || createTask.isPending}>
            Opprett
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
